import React from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl-next';

import { Box, Flex, xcss } from '@atlaskit/primitives';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	DropdownItemCheckbox,
	DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import ChevronDown from '@atlaskit/icon/utility/migration/chevron-down';
import Button from '@atlaskit/button/new';

import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import {
	useCommentContentContext,
	useCommentContentDispatchContext,
} from '@confluence/comment-context';

import { CurrentView } from '../hooks/useCommentsPanel';

const i18n = defineMessages({
	showInlineComments: {
		id: 'comments-panel.show-inline-comments.label',
		defaultMessage: 'Show inline comments',
		description: 'Label for the show inline comments dropdown option',
	},
	showPageComments: {
		id: 'comments-panel.show-page-comments.label',
		defaultMessage: 'Show page comments',
		description: 'Label for the show page comments dropdown option',
	},
	pageOrder: {
		id: 'comments-panel.page-order.label',
		defaultMessage: 'Page Order',
		description: 'Label for the page order sort option',
	},
});

const dropDownMenuStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	paddingBottom: 'space.150',
	paddingLeft: 'space.300',
	paddingTop: 'space.050',
	gap: 'space.100',
});

const commentsPanelViewLabels: Record<CurrentView, string> = {
	[CurrentView.UNREAD]: 'Unread',
	[CurrentView.OPEN]: 'Open',
	[CurrentView.RESOLVED]: 'Resolved',
};

export type ViewFilter = {
	label: MessageDescriptor;
	name: CurrentView;
};

export type DropdownMenusProps = {
	selectedView?: CurrentView;
	setView?: (view: CurrentView) => void;
	views?: ViewFilter[];
	additionalActions?: ((...args: any[]) => void)[];
};

export const DropdownMenus: FC<DropdownMenusProps> = ({
	selectedView,
	setView,
	views,
	additionalActions,
}) => {
	const { formatMessage } = useIntl();
	const { showModal } = useDialogs();
	const { hasContentChanged } = useCommentContentContext();
	const { resetContentChanged } = useCommentContentDispatchContext();

	const updateView = (view: ViewFilter) => {
		const handleClick = () => {
			// Do nothing if the same view is clicked
			if (selectedView !== view.name) {
				setView && setView(view.name);

				if (additionalActions) {
					additionalActions.forEach((action) => action());
				}
			}
		};

		if (hasContentChanged) {
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					handleClick();
				},
			});
		} else {
			handleClick();
		}
	};

	return (
		<Flex role="tablist" xcss={dropDownMenuStyles}>
			<DropdownMenu
				trigger={({ triggerRef, ...props }) => (
					<Box>
						<Button spacing="compact" {...props} iconAfter={ChevronDown} ref={triggerRef}>
							{selectedView && commentsPanelViewLabels[selectedView]}
						</Button>
					</Box>
				)}
				testId="view-selector-dropdown"
				placement="bottom-end"
				shouldRenderToParent
			>
				<DropdownItemGroup title="Filter By">
					{views &&
						views.map((view) => (
							<DropdownItem
								key={view.name}
								onClick={() => updateView(view)}
								data-testId={`menu-item-${view.name}`}
							>
								{formatMessage(view.label)}
							</DropdownItem>
						))}
				</DropdownItemGroup>
				<DropdownItemCheckboxGroup id="actions" hasSeparator>
					<DropdownItemCheckbox id="show-inline-comments">
						{formatMessage(i18n.showInlineComments)}
					</DropdownItemCheckbox>
					<DropdownItemCheckbox id="show-page-comments">
						{formatMessage(i18n.showPageComments)}
					</DropdownItemCheckbox>
				</DropdownItemCheckboxGroup>
			</DropdownMenu>
			<Box>
				<Button spacing="compact">{formatMessage(i18n.pageOrder)}</Button>
			</Box>
		</Flex>
	);
};
